/**
 * Facial Expression Controller with 10 distinct emotional states and pupil gaze targeting.
 */
export type EmotionType =
  | 'neutral'
  | 'happy'
  | 'sad'
  | 'cry'
  | 'laugh'
  | 'angry'
  | 'thinking'
  | 'confused'
  | 'excited'
  | 'surprised';

export interface FacialState {
  eyebrowAngleLeft: number; // degrees
  eyebrowAngleRight: number;
  eyebrowY: number;
  eyeScaleY: number; // 0 (closed) to 1.3 (wide)
  pupilOffsetX: number;
  pupilOffsetY: number;
  mouthSmileCurvature: number; // -1 (sad) to 1 (happy)
  blush: boolean;
  tears: boolean;
}

export class ExpressionController {
  private current: FacialState;
  private emotion: EmotionType = 'neutral';

  constructor() {
    this.current = this.getEmotionState('neutral');
  }

  public setEmotion(emotion: EmotionType): void {
    this.emotion = emotion;
    this.current = this.getEmotionState(emotion);
  }

  public getEmotion(): EmotionType {
    return this.emotion;
  }

  public getState(): FacialState {
    return { ...this.current };
  }

  public lookAt(direction: 'left' | 'right' | 'up' | 'down' | 'center'): void {
    switch (direction) {
      case 'left':
        this.current.pupilOffsetX = -4;
        this.current.pupilOffsetY = 0;
        break;
      case 'right':
        this.current.pupilOffsetX = 4;
        this.current.pupilOffsetY = 0;
        break;
      case 'up':
        this.current.pupilOffsetX = 0;
        this.current.pupilOffsetY = -4;
        break;
      case 'down':
        this.current.pupilOffsetX = 0;
        this.current.pupilOffsetY = 4;
        break;
      case 'center':
      default:
        this.current.pupilOffsetX = 0;
        this.current.pupilOffsetY = 0;
        break;
    }
  }

  public blink(progress: number): void {
    // 0 = open, 1 = shut closed
    const base = this.getEmotionState(this.emotion).eyeScaleY;
    this.current.eyeScaleY = base * (1 - progress);
  }

  private getEmotionState(emotion: EmotionType): FacialState {
    switch (emotion) {
      case 'happy':
        return {
          eyebrowAngleLeft: -8,
          eyebrowAngleRight: 8,
          eyebrowY: -3,
          eyeScaleY: 0.9,
          pupilOffsetX: 0,
          pupilOffsetY: 0,
          mouthSmileCurvature: 0.9,
          blush: true,
          tears: false
        };

      case 'excited':
        return {
          eyebrowAngleLeft: -12,
          eyebrowAngleRight: 12,
          eyebrowY: -6,
          eyeScaleY: 1.2,
          pupilOffsetX: 0,
          pupilOffsetY: -2,
          mouthSmileCurvature: 1.0,
          blush: true,
          tears: false
        };

      case 'sad':
        return {
          eyebrowAngleLeft: 14,
          eyebrowAngleRight: -14,
          eyebrowY: 2,
          eyeScaleY: 0.8,
          pupilOffsetX: 0,
          pupilOffsetY: 3,
          mouthSmileCurvature: -0.8,
          blush: false,
          tears: false
        };

      case 'cry':
        return {
          eyebrowAngleLeft: 18,
          eyebrowAngleRight: -18,
          eyebrowY: 3,
          eyeScaleY: 0.3,
          pupilOffsetX: 0,
          pupilOffsetY: 4,
          mouthSmileCurvature: -1.0,
          blush: false,
          tears: true
        };

      case 'angry':
        return {
          eyebrowAngleLeft: -18,
          eyebrowAngleRight: 18,
          eyebrowY: 4,
          eyeScaleY: 0.85,
          pupilOffsetX: 0,
          pupilOffsetY: 0,
          mouthSmileCurvature: -0.5,
          blush: false,
          tears: false
        };

      case 'thinking':
        return {
          eyebrowAngleLeft: -12,
          eyebrowAngleRight: 6,
          eyebrowY: -2,
          eyeScaleY: 0.9,
          pupilOffsetX: 3,
          pupilOffsetY: -4,
          mouthSmileCurvature: 0.2,
          blush: false,
          tears: false
        };

      case 'confused':
        return {
          eyebrowAngleLeft: -14,
          eyebrowAngleRight: 14,
          eyebrowY: -1,
          eyeScaleY: 1.1,
          pupilOffsetX: -3,
          pupilOffsetY: 0,
          mouthSmileCurvature: -0.3,
          blush: false,
          tears: false
        };

      case 'surprised':
        return {
          eyebrowAngleLeft: -16,
          eyebrowAngleRight: 16,
          eyebrowY: -8,
          eyeScaleY: 1.3,
          pupilOffsetX: 0,
          pupilOffsetY: 0,
          mouthSmileCurvature: 0.5,
          blush: false,
          tears: false
        };

      case 'laugh':
        return {
          eyebrowAngleLeft: -10,
          eyebrowAngleRight: 10,
          eyebrowY: -4,
          eyeScaleY: 0.4,
          pupilOffsetX: 0,
          pupilOffsetY: 0,
          mouthSmileCurvature: 1.0,
          blush: true,
          tears: false
        };

      case 'neutral':
      default:
        return {
          eyebrowAngleLeft: 0,
          eyebrowAngleRight: 0,
          eyebrowY: 0,
          eyeScaleY: 1.0,
          pupilOffsetX: 0,
          pupilOffsetY: 0,
          mouthSmileCurvature: 0.1,
          blush: false,
          tears: false
        };
    }
  }
}
